import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { adaptEchartsOptions, BaseEchartsDirective } from '@examdojo/core/widget';
import { EChartsOption } from 'echarts';
import { BarChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import {
  CHART_BAR_BORDER_RADIUS,
  CHART_COLORS,
  CHART_GRID_COMPACT,
  CHART_TOOLTIP_STYLE,
  CHART_X_AXIS_STYLE,
  CHART_Y_AXIS_STYLE,
  fmt,
  tooltipRow,
} from '../../../shared/chart-shared';
import { BAR_CHART_PALETTE, type BarChartParams, type BarChartSeries } from './bar-chart.model';

echarts.use([BarChart, GridComponent, LegendComponent, TooltipComponent]);

export interface BarSelection {
  readonly dataIndex: number;
  readonly label: string;
  readonly value: number;
  readonly seriesName: string;
}

@Component({
  selector: 'dojo-bar-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  template: `
    <div
      echarts
      [options]="adaptedOptions()"
      [initOpts]="initOpts"
      (chartClick)="onChartClick($event)"
      class="bar-chart"
    ></div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }

    .bar-chart {
      width: 100%;
      height: 100%;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarChartComponent extends BaseEchartsDirective {
  readonly params = input.required<BarChartParams>();

  readonly barSelect = output<BarSelection>();

  readonly chartOptions = computed<EChartsOption>(() => {
    const { categories, series, xAxisLabel, yAxisLabel = 'Frequency' } = this.params();

    if (categories.length === 0 || series.length === 0) {
      return buildEmptyChart(xAxisLabel, yAxisLabel);
    }

    const multiSeries = series.length > 1;

    return {
      animation: false,
      tooltip: {
        ...CHART_TOOLTIP_STYLE,
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(0, 0, 0, 0.04)' } },
        formatter: (tooltipParams) => {
          const params = tooltipParams as Array<{ seriesName: string; value: number; name: string; color: string }>;
          if (!params.length) {
            return '';
          }
          const header = `<div style="margin-bottom:4px;font-weight:600;color:var(--neutral-900)">${params[0].name}</div>`;
          const rows = params.map((p) => tooltipRow(p.seriesName, fmt(p.value))).join('');
          return header + rows;
        },
      },
      legend: multiSeries
        ? {
            show: true,
            top: 0,
            textStyle: { fontSize: 12, color: CHART_COLORS.label },
          }
        : { show: false },
      grid: {
        ...CHART_GRID_COMPACT,
        top: multiSeries ? 36 : CHART_GRID_COMPACT.top,
      },
      xAxis: {
        ...CHART_X_AXIS_STYLE,
        type: 'category',
        data: categories,
        name: xAxisLabel,
        nameLocation: 'middle',
        nameGap: 30,
        axisLabel: {
          ...CHART_X_AXIS_STYLE.axisLabel,
          rotate: categories.length > 15 ? 45 : 0,
          hideOverlap: true,
        },
      },
      yAxis: {
        ...CHART_Y_AXIS_STYLE,
        type: 'value',
        name: yAxisLabel,
        nameLocation: 'middle',
        nameGap: 35,
      },
      series: series.map((s, seriesIndex) => buildBarSeries(s, seriesIndex)),
    };
  });

  readonly adaptedOptions = computed(() => adaptEchartsOptions(this.chartOptions(), this.document, this.isDark()));

  onChartClick(event: { seriesType?: string; seriesName?: string; dataIndex: number }): void {
    if (event.seriesType !== 'bar') {
      return;
    }
    const { categories } = this.params();
    const i = event.dataIndex;
    if (i < 0 || i >= categories.length) {
      return;
    }
    const seriesEntry = this.params().series.find((s) => s.name === event.seriesName);
    this.barSelect.emit({
      dataIndex: i,
      label: categories[i],
      value: seriesEntry?.values[i] ?? 0,
      seriesName: event.seriesName ?? '',
    });
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildBarSeries(s: BarChartSeries, seriesIndex: number): Record<string, unknown> {
  const baseColor = s.color ?? BAR_CHART_PALETTE[seriesIndex % BAR_CHART_PALETTE.length];
  const emphasisSet = new Set(s.emphasis?.indices ?? []);
  const emphasisColor = s.emphasis?.color ?? darken(baseColor, 0.25);
  const hasEmphasis = emphasisSet.size > 0;

  return {
    type: 'bar',
    name: s.name,
    data: s.values.map((value, i) => {
      const isEmphasised = emphasisSet.has(i);
      return {
        value,
        itemStyle: isEmphasised ? { color: emphasisColor } : undefined,
        label:
          isEmphasised && s.emphasis?.label
            ? {
                show: true,
                position: 'top' as const,
                formatter: s.emphasis.label,
                fontSize: 12,
                fontWeight: 'bold' as const,
                color: emphasisColor,
              }
            : undefined,
      };
    }),
    itemStyle: {
      color: baseColor,
      borderRadius: CHART_BAR_BORDER_RADIUS,
    },
    label: hasEmphasis
      ? { show: false }
      : {
          show: true,
          position: 'top',
          fontSize: 12,
          color: CHART_COLORS.label,
        },
  };
}

function buildEmptyChart(xAxisLabel: string | undefined, yAxisLabel: string): EChartsOption {
  return {
    grid: CHART_GRID_COMPACT,
    xAxis: {
      ...CHART_X_AXIS_STYLE,
      type: 'category',
      data: [],
      name: xAxisLabel,
      nameLocation: 'middle',
      nameGap: 30,
    },
    yAxis: {
      ...CHART_Y_AXIS_STYLE,
      type: 'value',
      name: yAxisLabel,
      nameLocation: 'middle',
      nameGap: 35,
    },
    series: [],
  };
}

/** Darken a hex color by a factor (0–1). */
function darken(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((num >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round((num & 0xff) * (1 - amount)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
